import { useQuery } from '@tanstack/react-query';
import type { Competition } from '@uefadigital/panenka';
import { memo } from 'react';
import { Route, Routes, BrowserRouter as Router } from 'react-router';

import { useRoutedContent } from '@hooks/useRoutedContent';
import { getDomesticCompetitionsQuery } from '@queries/competition';
import { hydrateWithTranslations } from '@services/TranslatorService';
import PanenkaDropdown from '@shared/PanenkaDropdown';
import { useTranslateFn } from '@shared/Translate';
import GoogleTagManagerHelper from '@tracking/gtm-helper';

import * as styles from './styles.module.scss';

const ButtonContent = ({ element: c }: { element: Competition }) => {
  const t = useTranslateFn();

  return (
    <div className={styles.triggerWrapper}>
      <span className={styles.label}>{t('selected-competition')}</span>
      <span className={styles.name}>{hydrateWithTranslations(c)?.name}</span>
    </div>
  );
};
const MenuItemContent = ({ element: c }: { element: Competition }) => (
  <span className={styles.menuItem}>{hydrateWithTranslations(c).name}</span>
);

const Dropdown = ({ options }) => {
  const countryCode = options.association.countryCode;
  const { data: competitions } = useQuery({
    ...getDomesticCompetitionsQuery({
      age: 'ADULT',
      countryCode,
      limit: 10,
      offset: 0,
    }),
    enabled: !!countryCode,
  });

  const onContentLoaded = (content: string) => {
    const target = document.querySelector<HTMLElement>(options.targetSelector);
    target.innerHTML = content;
  };

  const { selectedItem: selectedCompetition, updateSelectedItem: updateSelectedCompetition } =
    useRoutedContent<Competition>(
      competitions,
      (c) => `/${c.type.toLowerCase()}/${c.id}/`,
      (c) => options.libraryPath.replace('{type}', c.type.toLowerCase()).replace('{id}', c.id),
      onContentLoaded,
      options.useRouting,
    );

  const handleSelectedCompetition = (competition: Competition) => {
    GoogleTagManagerHelper.event(
      'Click',
      'Select competition',
      'ma domestic',
      { interaction: 'ma domestic' },
      'select_competition',
    );
    updateSelectedCompetition(competition);
  };

  return (
    <div className={styles.wrapper}>
      {!!competitions?.length && (
        <PanenkaDropdown<Competition>
          elements={competitions}
          selectedItem={selectedCompetition}
          onSelect={handleSelectedCompetition}
          iconColor='text-01'
          ButtonContent={ButtonContent}
          MenuItemContent={MenuItemContent}
        />
      )}
    </div>
  );
};

const RoutedDropdown = ({ options }) => (
  <Router basename={options.useRouting ? options.deepLinkBaseUrl : location.pathname}>
    <Routes>
      <Route path='/' element={<Dropdown options={options} />} />
      {options.useRouting && <Route path=':type/:id' element={<Dropdown options={options} />} />}
    </Routes>
  </Router>
);

export default memo(RoutedDropdown);
